import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { HpButton, HpCardHeader, HpField } from '@hp/ui';
import { MERGE_JOB_MODES, type MergeJobMode, type SubjectKind } from '@hp/domain';
import { invalidatePipelineState } from '../pipeline/queries';
import { trpc } from '../../api/trpc';
import { invalidateMerge } from './queries';
import { ErrorAlert } from '../../components/error-alert';

export function NewJobPanel() {
  const { t } = useTranslation(['jobs', 'common']);
  const navigate = useNavigate();

  const [mode, setMode] = useState<MergeJobMode>('dry_run');
  const [entityType, setEntityType] = useState<SubjectKind>('person');
  const [cap, setCap] = useState('100');

  const create = useMutation(
    trpc.merge.createJob.mutationOptions({
      onSuccess: (res) => {
        void invalidateMerge();
        void invalidatePipelineState();
        void navigate(`/crm-data/jobs/${res.id}`);
      },
    }),
  );

  function submit() {
    const body: { mode: MergeJobMode; entityType: SubjectKind; cap?: number } = { mode, entityType };
    if (mode === 'pilot') {
      const parsed = Number(cap);
      body.cap = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 100;
    }
    create.mutate(body);
  }

  return (
    <section className="hp-card">
      <HpCardHeader title={t('new.title')} icon="plus" />
      <div className="hp-card__body bo-sections">
        <div className="bo-inline-form">
          <HpField label={t('new.mode')}>
            <select value={mode} onChange={(e) => setMode(e.target.value as MergeJobMode)}>
              {MERGE_JOB_MODES.map((m) => (
                <option key={m} value={m}>
                  {t(`modes.${m}`)}
                </option>
              ))}
            </select>
          </HpField>
          <HpField label={t('new.entityType')}>
            <select value={entityType} onChange={(e) => setEntityType(e.target.value as SubjectKind)}>
              <option value="person">{t('new.persons')}</option>
              <option value="organization">{t('new.organizations')}</option>
            </select>
          </HpField>
          {mode === 'pilot' && (
            <HpField label={t('new.cap')} hint={t('new.capHint')}>
              <input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} />
            </HpField>
          )}
          <div className="bo-inline-form__submit">
            <HpButton variant="primary" icon="play" busy={create.isPending} onClick={submit}>
              {create.isPending ? t('new.creating') : t('new.create')}
            </HpButton>
          </div>
        </div>
        {mode === 'full' && <p className="bo-hint bo-hint--danger">{t('new.fullWarning')}</p>}
        <ErrorAlert error={create.error} />
      </div>
    </section>
  );
}
